import React, { useEffect, useState } from 'react';
import { Tab, Loader } from 'semantic-ui-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { getWithAuth, postWithAuth } from 'src/api';
import TddSyncTab from './TddSyncTab';
import { PanelHeading } from 'src/components/PanelHeading';
import './TddSyncPanel.css';
import { NavLink } from 'react-router-dom';

const fetchTddSyncSettings = async (projectId) => {
  return getWithAuth(`project/${projectId}/tdd_sync/settings`);
};

const fetchTddSyncLinks = async (projectId) => {
  return getWithAuth(`project/${projectId}/tdd_sync/links`);
};

const updateTddSyncSettings = async (projectId, bandType, settings) => {
  return postWithAuth(
    `project/${projectId}/tdd_sync/settings`,
    { band_type: bandType, settings },
    'PUT'
  );
};

const TddSyncPanel: React.FC = () => {
  const projectId = useSelector((state: any) => state.mainFrame.projectId);
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: settingsData,
    isLoading: settingsIsLoading,
    error: settingsError,
  } = useQuery(['tddSync', projectId], () => fetchTddSyncSettings(projectId), {
    enabled: !!projectId,
  });

  const {
    data: linksData,
    isLoading: linksIsLoading,
    error: linksError,
  } = useQuery(
    ['tddSyncLinks', projectId],
    () => fetchTddSyncLinks(projectId),
    { enabled: !!projectId }
  );
  const methods = useForm();

  const mutation = useMutation({
    mutationFn: ({ bandType, settings }: { bandType: string; settings: any }) =>
      updateTddSyncSettings(projectId, bandType, settings),
    onMutate: () => {
      setIsSaving(true);
    },
    onSuccess: () => {
      // Update the cache with fresh data from server
      queryClient.invalidateQueries({ queryKey: ['tddSync', projectId] });

      // Links may be recomputed based on new settings
      queryClient.invalidateQueries({ queryKey: ['tddSyncLinks', projectId] });

      // Reset form with new data
      methods.reset(settingsData);
      setIsSaving(false);
    },
    onError: (error) => {
      console.error('Failed to save TDD Sync settings:', error);
      setIsSaving(false);
    },
  });

  // Handler for when a setting changes - to be passed to TddSyncTab
  const handleSettingChange = (bandType: string, settings: any) => {
    mutation.mutate({ bandType, settings });
  };

  // Reset form with fetched data when it becomes available
  useEffect(() => {
    if (settingsData) {
      methods.reset(settingsData);
    }
  }, [settingsData]);

  // Clear the query cache when navigating away from the panel
  useEffect(() => {
    return () => {
      queryClient.removeQueries(['tddSync', projectId]);
      queryClient.removeQueries(['tddSyncLinks', projectId]);
    };
  }, [projectId, queryClient]);

  if (settingsIsLoading) {
    return <Loader active />;
  }

  if (settingsError || !settingsData) {
    return (
      <div className="tddsync-empty-message">
        This project has no synchronized links
      </div>
    );
  }

  let panes = [
    {
      menuItem: {
        as: NavLink,
        id: 'tddsyncUnlicensedTab',
        content: '4.4 to 6 GHz',
        to: '/tddsync/4to6ghz',
        key: 'tddsyncUnlicensed',
      },
      render: () => (
        <Tab.Pane className="tdd-sync-tab">
          <TddSyncTab
            syncParams={settingsData?.unlicensed}
            fieldPrefix="unlicensed"
            onSettingChange={handleSettingChange}
            isSaving={isSaving}
            links={linksData?.unlicensed}
            metrics={linksData?.unlicensed_metrics}
            linksIsLoading={linksIsLoading}
            linksError={linksError}
          />
        </Tab.Pane>
      ),
    },

    {
      menuItem: {
        as: NavLink,
        id: 'tddsyncLicensedTab',
        content: '7 to 8.5 GHz',
        to: '/tddsync/7to8ghz',
        key: 'tddsyncLicensed',
      },
      render: () => (
        <Tab.Pane className="tdd-sync-tab">
          <TddSyncTab
            syncParams={settingsData?.licensed}
            fieldPrefix="licensed"
            onSettingChange={handleSettingChange}
            isSaving={isSaving}
            links={linksData?.licensed}
            metrics={linksData?.licensed_metrics}
            linksIsLoading={linksIsLoading}
            linksError={linksError}
          />
        </Tab.Pane>
      ),
    },
  ];

  const getActiveIndex = () => {
    const idx = panes.findIndex((el) => el.menuItem.to === location.pathname);
    return idx >= 0 ? idx : 0;
  };

  return (
    <div className="tdd-sync-panel">
      <PanelHeading title="TDD Synchronization" />
      <FormProvider {...methods}>
        <Tab
          panes={panes}
          className="tdd-sync-tab-container"
          activeIndex={getActiveIndex()}
        />
      </FormProvider>
    </div>
  );
};

export default TddSyncPanel;
